import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { Layout } from '@components';
import { email, siteUrl as SITE_URL } from '@config';

const StyledMain = styled.main`
  max-width: 920px;

  .hero {
    margin-top: 36px;

    .overline {
      margin: 0 0 14px;
      color: var(--icon-accent);
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      letter-spacing: 0.04em;
    }

    h1 {
      margin: 0 0 24px;
      font-size: clamp(34px, 7vw, 58px);
      line-height: 1.1;
      color: var(--lightest-slate);
    }

    p {
      max-width: 760px;
      margin: 0 0 16px;
    }
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
    margin: 34px 0;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .card {
    ${({ theme }) => theme.mixins.boxShadow};
    background: var(--surface);
    border: 1px solid var(--surface-border);
    border-radius: var(--border-radius);
    padding: 20px 20px 18px;

    h2 {
      margin: 0 0 12px;
      font-size: var(--fz-xxl);
    }

    ul {
      ${({ theme }) => theme.mixins.fancyList};
      margin: 0;
    }
  }

  .cta-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin: 24px 0 44px;

    a {
      ${({ theme }) => theme.mixins.bigButton};
      color: var(--icon-accent);
      border-color: var(--icon-accent);

      &:hover,
      &:focus-visible {
        box-shadow: 4px 4px 0 0 var(--icon-accent);
      }

      &.ghost {
        color: var(--lightest-slate);
        border-color: var(--surface-border);
      }
    }
  }

  .faq {
    margin-top: 10px;

    h2 {
      margin-bottom: 18px;
    }
  }

  .faq-item {
    ${({ theme }) => theme.mixins.boxShadow};
    background: var(--surface);
    border: 1px solid var(--surface-border);
    border-radius: var(--border-radius);
    padding: 16px 18px;
    margin-bottom: 10px;

    h3 {
      margin: 0 0 8px;
      font-size: var(--fz-lg);
      color: var(--lightest-slate);
    }

    p {
      margin: 0;
      color: var(--light-slate);
    }
  }

  .internal-links {
    margin: 36px 0 0;

    h2 {
      margin-bottom: 10px;
      font-size: var(--fz-xl);
    }

    ul {
      ${({ theme }) => theme.mixins.fancyList};
      margin: 0;

      a {
        color: var(--icon-accent);
      }
    }
  }
`;

const SeoLandingPage = ({ location, language, page }) => {
  const pageUrl = `${SITE_URL}${location.pathname}`;

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: page.faq.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const serviceSchema = {
    '@type': 'ProfessionalService',
    name: page.serviceSchema.name,
    serviceType: page.serviceSchema.serviceType,
    url: pageUrl,
    areaServed: {
      '@type': 'City',
      name: 'Paris',
    },
    provider: {
      '@type': 'Person',
      name: 'Hedi Hidouri',
      url: SITE_URL,
    },
  };

  const headProps = {
    title: page.seoTitle,
    description: page.seoDescription,
    pathname: location.pathname,
    schemaNodes: [serviceSchema, faqSchema],
  };

  return (
    <Layout location={location} language={language} headProps={headProps}>
      <StyledMain>
        <section className="hero">
          <p className="overline">{page.overline}</p>
          <h1>{page.heading}</h1>
          {page.intro.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </section>

        <section className="grid">
          <article className="card">
            <h2>{page.offerTitle}</h2>
            <ul>
              {page.offerItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="card">
            <h2>{page.outcomesTitle}</h2>
            <ul>
              {page.outcomeItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </article>
        </section>

        <div className="cta-row">
          <a href={`mailto:${email}`}>{page.primaryCta}</a>
          <Link className="ghost" to={page.secondaryCtaUrl}>
            {page.secondaryCta}
          </Link>
        </div>

        <section className="faq">
          <h2>{page.faqTitle}</h2>
          {page.faq.map((item, i) => (
            <article className="faq-item" key={i}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </section>

        <section className="internal-links">
          <h2>{page.relatedTitle}</h2>
          <ul>
            {page.relatedLinks.map((item, i) => (
              <li key={i}>
                <Link to={item.url}>{item.label}</Link>
              </li>
            ))}
          </ul>
        </section>
      </StyledMain>
    </Layout>
  );
};

SeoLandingPage.propTypes = {
  location: PropTypes.object.isRequired,
  language: PropTypes.oneOf(['en', 'fr']).isRequired,
  page: PropTypes.shape({
    seoTitle: PropTypes.string.isRequired,
    seoDescription: PropTypes.string.isRequired,
    overline: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    intro: PropTypes.arrayOf(PropTypes.string).isRequired,
    offerTitle: PropTypes.string.isRequired,
    offerItems: PropTypes.arrayOf(PropTypes.string).isRequired,
    outcomesTitle: PropTypes.string.isRequired,
    outcomeItems: PropTypes.arrayOf(PropTypes.string).isRequired,
    primaryCta: PropTypes.string.isRequired,
    secondaryCta: PropTypes.string.isRequired,
    secondaryCtaUrl: PropTypes.string.isRequired,
    faqTitle: PropTypes.string.isRequired,
    faq: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string.isRequired,
        answer: PropTypes.string.isRequired,
      }),
    ).isRequired,
    relatedTitle: PropTypes.string.isRequired,
    relatedLinks: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ).isRequired,
    serviceSchema: PropTypes.shape({
      name: PropTypes.string.isRequired,
      serviceType: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default SeoLandingPage;
